import { RefObject, useCallback, useEffect, useState } from 'react';

import { isEqual } from 'lodash-es';
import { useWindowDimensions } from 'react-native';

/**
 * Reactively returns whether or not the element is overflowing left or right.
 *
 * Pass in a ref to the element you want to watch.
 *
 * TODO add vertical checks if ever necessary.
 */
export const useScrollOverflow = <T extends RefObject<HTMLElement>>(ref: T) => {
  const [overflow, setOverflow] = useState({
    isOverflowingLeft: false,
    isOverflowingRight: false,
    isOverflowing: false,
  });

  const { width: windowWidth } = useWindowDimensions();

  /**
   * Checks element's overflow conditions and sets state.
   */
  const checkOverflowFn = useCallback(() => {
    const element = ref.current;

    if (!element) {
      return;
    }
    const { clientWidth, scrollWidth, scrollLeft } = element;

    const isOverflowing = clientWidth < scrollWidth;
    const isOverflowingLeft = isOverflowing && scrollLeft > 0;
    const isOverflowingRight = isOverflowing && scrollLeft + clientWidth < scrollWidth;

    const updateIsOverflowing = {
      isOverflowing,
      isOverflowingLeft,
      isOverflowingRight,
    };

    // only update state if it's different since it's an object.
    if (!isEqual(overflow, updateIsOverflowing)) {
      setOverflow(updateIsOverflowing);
    }
  }, [setOverflow, overflow, ref]);

  // set up scroll listeners
  useEffect(() => {
    const element = ref.current;

    if (element && overflow.isOverflowing) {
      ['touchmove', 'scroll'].forEach(event => element.addEventListener(event, checkOverflowFn));

      return () => {
        ['touchmove', 'scroll'].forEach(event =>
          element.removeEventListener(event, checkOverflowFn)
        );
      };
    }

    // lint error fix...
    return () => {};
  }, [ref, overflow, checkOverflowFn]);

  // On screen width change
  useEffect(() => {
    checkOverflowFn();
  }, [windowWidth, checkOverflowFn]);

  return overflow;
};
